import { pickInternalDestinationFromResolveResponse, resolveDestination } from "../services/destinationsApi";

const DEV =
  typeof import.meta !== "undefined" &&
  Boolean(import.meta.env?.DEV);

function nonEmpty(value) {
  const s = value == null ? "" : String(value).trim();
  return s || "";
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const s = nonEmpty(value);
    if (s) return s;
  }
  return "";
}

function numberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeLdsCode(value) {
  const s = nonEmpty(value).toUpperCase();
  return /^[A-Z]{3}$/.test(s) ? s : "";
}

function normalizeDestinationId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  return nonEmpty(value) || null;
}

export function traceDestinationSearch(tag, payload) {
  if (!DEV) return;
  try {
    console.log(tag, payload);
  } catch {
    console.log(tag);
  }
}

export function getDestinationLabel(location) {
  const loc = Array.isArray(location) ? location[0] : location;
  if (!loc || typeof loc !== "object") return nonEmpty(loc);
  return firstNonEmpty(
    loc.fullname,
    loc.description,
    loc.label,
    loc.displayName,
    loc.name,
    [loc.CITYNAME, loc.COUNTRYNAME].filter(Boolean).join(", "),
    [loc.CITYNAME_HEB, loc.COUNTRYNAME_HEB].filter(Boolean).join(", "),
    [loc.city, loc.country].filter(Boolean).join(", ")
  );
}

export function getDestinationEntityFromLocation(location) {
  const loc = Array.isArray(location) ? location[0] : location;
  if (!loc || typeof loc !== "object") return null;
  const nested =
    loc.destinationEntity && typeof loc.destinationEntity === "object"
      ? loc.destinationEntity
      : {};
  const destinationId = normalizeDestinationId(
    nested.destinationId ??
      nested.destination_id ??
      nested.internal_destination_id ??
      loc.destinationId ??
      loc.destination_id ??
      loc.internal_destination_id
  );
  const ldsCode = normalizeLdsCode(
    nested.ldsCode ??
      nested.lds_code ??
      loc.ldsCode ??
      loc.lds_code ??
      loc.AIRPORTCODE ??
      loc.CITYCODE ??
      loc.code
  );
  const name = firstNonEmpty(
    nested.name,
    nested.destinationName,
    nested.destination_name,
    loc.destinationName,
    loc.destination_name,
    loc.CITYNAME,
    loc.city,
    loc.name,
    loc.displayName
  );
  const country = firstNonEmpty(
    nested.country,
    nested.countryName,
    nested.country_name,
    loc.country,
    loc.COUNTRYNAME,
    loc.countryName
  );
  const latitude = numberOrNull(nested.latitude ?? nested.lat ?? loc.latitude ?? loc.lat);
  const longitude = numberOrNull(
    nested.longitude ?? nested.lon ?? nested.lng ?? loc.longitude ?? loc.lon ?? loc.lng
  );
  const placeId = firstNonEmpty(
    nested.placeId,
    nested.place_id,
    loc.placeId,
    loc.place_id,
    loc.id
  );

  if (!destinationId && !ldsCode && !name && !country && latitude == null && longitude == null) {
    return null;
  }

  return {
    destinationId,
    ldsCode,
    name,
    country,
    latitude,
    longitude,
    placeId,
    source: firstNonEmpty(nested.source, loc.source) || "selected_destination",
  };
}

export function buildDestinationEntityFromPlace(place) {
  if (!place || typeof place !== "object") return null;
  const types = Array.isArray(place.types) ? place.types : [];
  const displayName = firstNonEmpty(place.displayName, place.name);
  const city = firstNonEmpty(place.city, types.includes("locality") ? displayName : "");
  const regionName = types.includes("administrative_area_level_1") ? displayName : "";
  const name = firstNonEmpty(city, regionName, displayName, place.fullname);
  const latitude = numberOrNull(place.latitude);
  const longitude = numberOrNull(place.longitude);

  return {
    destinationId: null,
    ldsCode: "",
    name,
    country: firstNonEmpty(place.country),
    latitude,
    longitude,
    placeId: firstNonEmpty(place.id, place.place_id, place.placeId),
    source: "google_places",
  };
}

export async function resolveDestinationEntity(entity) {
  if (!entity || typeof entity !== "object") return null;
  const base = {
    destinationId: normalizeDestinationId(entity.destinationId ?? entity.destination_id),
    ldsCode: normalizeLdsCode(entity.ldsCode ?? entity.lds_code),
    name: firstNonEmpty(entity.name, entity.destinationName, entity.destination_name),
    country: firstNonEmpty(entity.country, entity.countryName, entity.country_name),
    latitude: numberOrNull(entity.latitude ?? entity.lat),
    longitude: numberOrNull(entity.longitude ?? entity.lon ?? entity.lng),
    placeId: firstNonEmpty(entity.placeId, entity.place_id),
    source: firstNonEmpty(entity.source) || "selected_destination",
  };

  if (base.destinationId || (!base.ldsCode && base.name.length < 2)) {
    return base;
  }

  try {
    const resolved = await resolveDestination({
      lds_code: base.ldsCode || undefined,
      name: base.name,
    });
    const destinationId = pickInternalDestinationFromResolveResponse(resolved);
    return {
      ...base,
      destinationId: destinationId ?? base.destinationId,
      ldsCode: normalizeLdsCode(resolved?.lds_code) || base.ldsCode,
      name: firstNonEmpty(resolved?.name, base.name),
      resolveStatus: resolved?.status || null,
    };
  } catch (error) {
    traceDestinationSearch("[DESTINATION_AUTOCOMPLETE_TRACE]", {
      phase: "entity_resolve_failed",
      name: base.name,
      error: error?.message || String(error),
    });
    return base;
  }
}

export function summarizeDestinationEntity(entity) {
  if (!entity) return null;
  return {
    destinationId: entity.destinationId ?? null,
    ldsCode: entity.ldsCode || null,
    name: entity.name || null,
    country: entity.country || null,
    hasCoordinates: entity.latitude != null && entity.longitude != null,
    source: entity.source || null,
    resolveStatus: entity.resolveStatus || null,
  };
}
